import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { invalidateFor } from "@/lib/cache-registry";
import { ensurePoCustomerLink } from "@/lib/ensure-po-customer";
import {
  updateWithOptimisticLock,
  OptimisticLockConflictError,
} from "@/lib/optimistic-lock";

interface UpdateProjectCustomerArgs {
  projectId: string;
  customerId: string;
  /** VIB-757: project.updated_at fra siste hentet data — brukes til konflikt-deteksjon. */
  expectedUpdatedAt: string;
}

interface OptimisticContext {
  prev: unknown;
}

/**
 * Setter `projects.customer_id` med optimistisk update mot
 * `["project-detail", projectId]`-cachen. Brukes av `CustomerPicker`
 * i prosjekt-administrasjonen.
 *
 * Rollback ved feil. Ved OptimisticLockConflictError (en annen bruker
 * har endret raden mellom lese og skrive) vises egen toast og cache
 * invalideres så fresh data hentes.
 *
 * Etter at kunden er koblet pushes den til PowerOffice via
 * `ensure-po-customer` (best-effort). Dette tetter hullet der en kunde
 * koblet til et eksisterende prosjekt aldri fikk PO-kobling og dermed
 * blokkerte fakturering.
 */
export function useUpdateProjectCustomer() {
  const qc = useQueryClient();
  return useMutation<void, Error, UpdateProjectCustomerArgs, OptimisticContext>({
    mutationFn: async ({ projectId, customerId, expectedUpdatedAt }) => {
      await updateWithOptimisticLock(
        "projects",
        projectId,
        { customer_id: customerId },
        expectedUpdatedAt,
      );
    },
    onMutate: async ({ projectId, customerId }) => {
      const key = ["project-detail", projectId];
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData(key);
      qc.setQueryData(key, (old: unknown) => {
        if (!old || typeof old !== "object") return old;
        const project = (old as { project?: Record<string, unknown> }).project;
        if (!project) {
          return { ...(old as Record<string, unknown>), customer_id: customerId };
        }
        return {
          ...(old as Record<string, unknown>),
          project: { ...project, customer_id: customerId },
        };
      });
      return { prev };
    },
    onError: (err, { projectId }, ctx) => {
      qc.setQueryData(["project-detail", projectId], ctx?.prev);
      if (err instanceof OptimisticLockConflictError) {
        toast.error(
          "En annen bruker har endret prosjektet. Last inn på nytt og prøv igjen.",
        );
      } else {
        toast.error(`Kunne ikke endre kunde: ${err.message}`);
      }
    },
    onSuccess: async (_data, { customerId }) => {
      const link = await ensurePoCustomerLink(customerId);
      if (link.method === "error") {
        toast.warning(
          "Kunde koblet, men ikke til PowerOffice. Fakturering vil ikke fungere før dette er fikset.",
          { description: link.error },
        );
      }
    },
    onSettled: async (_data, _err, { projectId }) => {
      await invalidateFor(qc, "projects", { projectId });
    },
  });
}
